import { useState, useMemo } from "react";
import { SearchIcon, UploadIcon, VideoIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import { VideoCard, Video } from "@/components/academician/video-card";
import {
  VideoFormModal,
  VideoFormData,
} from "@/components/academician/video-form-modal";
import { cn } from "@/lib/utils";

interface VideosLibraryPageProps {
  /**
   * Initial videos data
   */
  videos?: Video[];

  /**
   * Upload video callback
   */
  onUpload?: (data: VideoFormData) => void;

  /**
   * Update video callback
   */
  onUpdate?: (id: string, data: VideoFormData) => void;

  /**
   * Delete video callback
   */
  onDelete?: (id: string) => void;

  /**
   * Preview video callback
   */
  onPreview?: (video: Video) => void;

  /**
   * Additional CSS classes
   */
  className?: string;
}

// Sample data
const defaultVideos: Video[] = [
  {
    id: "1",
    title: "Introduction to Customer Discovery",
    description:
      "Learn the fundamentals of customer discovery and how to validate your startup idea before building.",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    thumbnail:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=450&fit=crop",
    duration: "12:34",
    viewCount: 1234,
    status: "published",
    createdAt: "2024-01-15",
  },
  {
    id: "2",
    title: "Problem Validation Techniques",
    description:
      "Master the art of validating problems with real customers through effective interviews.",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    thumbnail:
      "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=450&fit=crop",
    duration: "18:45",
    viewCount: 856,
    status: "draft",
    createdAt: "2024-01-20",
  },
  {
    id: "3",
    title: "Building Your MVP",
    description:
      "Learn how to build a minimum viable product that tests your core assumptions.",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    thumbnail:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=450&fit=crop",
    duration: "25:12",
    viewCount: 2341,
    status: "published",
    createdAt: "2024-01-25",
  },
  {
    id: "4",
    title: "Customer Interview Best Practices",
    description:
      "Discover the best practices for conducting effective customer interviews.",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    thumbnail:
      "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=450&fit=crop",
    duration: "15:30",
    viewCount: 1567,
    status: "published",
    createdAt: "2024-02-01",
  },
  {
    id: "5",
    title: "Market Sizing and TAM Analysis",
    description:
      "Learn how to calculate your total addressable market and validate market size.",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    thumbnail:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=450&fit=crop",
    duration: "20:15",
    viewCount: 945,
    status: "draft",
    createdAt: "2024-02-05",
  },
  {
    id: "6",
    title: "Pivot or Persevere: Making the Call",
    description:
      "Understand when to pivot your startup idea and when to persevere with your current direction.",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    thumbnail:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=450&fit=crop",
    duration: "22:48",
    viewCount: 1823,
    status: "published",
    createdAt: "2024-02-10",
  },
];

type FilterStatus = "all" | "published" | "draft";

export function VideosLibraryPage({
  videos = defaultVideos,
  onUpload,
  onUpdate,
  onDelete,
  onPreview,
  className,
}: VideosLibraryPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [videosList, setVideosList] = useState<Video[]>(videos);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<VideoFormData | undefined>(
    undefined
  );

  // Filter and search videos
  const filteredVideos = useMemo(() => {
    return videosList.filter((video) => {
      // Filter by status
      if (filterStatus !== "all" && video.status !== filterStatus) {
        return false;
      }

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          video.title.toLowerCase().includes(query) ||
          video.description?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [videosList, searchQuery, filterStatus]);

  // Count videos by status
  const statusCounts = useMemo(() => {
    return {
      all: videosList.length,
      published: videosList.filter((v) => v.status === "published").length,
      draft: videosList.filter((v) => v.status === "draft").length,
    };
  }, [videosList]);

  const handleUpload = (data: VideoFormData) => {
    const newVideo: Video = {
      id: Date.now().toString(),
      title: data.title,
      description: data.description,
      videoUrl: data.videoUrl,
      thumbnail: data.thumbnail,
      duration: data.duration,
      viewCount: 0,
      status: data.status,
      transcript: data.transcript,
      createdAt: new Date().toISOString(),
    };

    setVideosList((prev) => [newVideo, ...prev]);
    onUpload?.(data);
  };

  const handleEdit = (video: Video) => {
    setEditingVideo({
      id: video.id,
      title: video.title,
      description: video.description || "",
      videoUrl: video.videoUrl,
      thumbnail: video.thumbnail,
      duration: video.duration,
      status: video.status,
      transcript: video.transcript,
    });
    setIsModalOpen(true);
  };

  const handleUpdate = (data: VideoFormData) => {
    if (data.id) {
      setVideosList((prev) =>
        prev.map((v) => (v.id === data.id ? { ...v, ...data } : v))
      );
      onUpdate?.(data.id, data);
    }
    setEditingVideo(undefined);
  };

  const handleDelete = (video: Video) => {
    if (confirm(`Are you sure you want to delete "${video.title}"?`)) {
      setVideosList((prev) => prev.filter((v) => v.id !== video.id));
      onDelete?.(video.id);
    }
  };

  const handlePreview = (video: Video) => {
    onPreview?.(video);
    // In a real app, this would open a preview modal or navigate to video player
    console.log("Preview video:", video);
  };

  const handleOpenUploadModal = () => {
    setEditingVideo(undefined);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingVideo(undefined);
  };

  return (
    <div className={cn("min-h-screen bg-background", className)}>
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Videos Library
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage and organize your video content
              </p>
            </div>
            <AcademicianStartupButton
              variant="primary"
              leftIcon={<UploadIcon />}
              onClick={handleOpenUploadModal}
            >
              Upload Video
            </AcademicianStartupButton>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search videos by title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Status Filters */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus("all")}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                filterStatus === "all"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              All ({statusCounts.all})
            </button>
            <button
              onClick={() => setFilterStatus("published")}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                filterStatus === "published"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              Published ({statusCounts.published})
            </button>
            <button
              onClick={() => setFilterStatus("draft")}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                filterStatus === "draft"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              Draft ({statusCounts.draft})
            </button>
          </div>
        </div>

        {/* Videos Grid */}
        {filteredVideos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                onEdit={handleEdit}
                onPreview={handlePreview}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          // Empty State
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="bg-muted rounded-full p-6 mb-4">
              <VideoIcon className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {searchQuery || filterStatus !== "all"
                ? "No videos found"
                : "No videos yet"}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {searchQuery || filterStatus !== "all"
                ? "Try adjusting your search or filters to find what you're looking for."
                : "Get started by uploading your first video to the library."}
            </p>
            {!searchQuery && filterStatus === "all" && (
              <AcademicianStartupButton
                variant="primary"
                leftIcon={<UploadIcon />}
                onClick={handleOpenUploadModal}
              >
                Upload Your First Video
              </AcademicianStartupButton>
            )}
          </div>
        )}
      </div>

      {/* Upload/Edit Modal */}
      <VideoFormModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        video={editingVideo}
        onSave={editingVideo ? handleUpdate : handleUpload}
        onCancel={handleCloseModal}
      />
    </div>
  );
}
